import { useState, useCallback, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { IconWeight } from "@phosphor-icons/react";
import {
  MagnifyingGlass,
  BookOpen,
  Plus,
  Copy,
  Check,
  CircleNotch,
  CursorText,
  ArrowSquareOut,
  CaretDown,
  Trash,
  ArrowClockwise,
  HashStraight,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { useEditorStore } from "../../stores/editor";
import { useCitationStore, type SearchResult, type BibEntry } from "../../stores/citation";
import { insertSnippet } from "../editor/editor-commands";
import {
  CITATION_STYLES,
  formatCitation,
  formatInlineRef,
  type CitationStyle,
} from "../../lib/citationFormat";

const spring = { type: "spring" as const, stiffness: 500, damping: 35, mass: 0.6 };

interface CitationPanelProps {
  documentContent?: string;
  docType?: string;
}

export function CitationPanel({ documentContent, docType }: CitationPanelProps) {
  const editorView = useEditorStore((s) => s.editorView);
  const isLatex = !docType || docType === "latex";

  const searchQuery = useCitationStore((s) => s.searchQuery);
  const setSearchQuery = useCitationStore((s) => s.setSearchQuery);
  const searchResults = useCitationStore((s) => s.searchResults);
  const searching = useCitationStore((s) => s.searching);
  const searchError = useCitationStore((s) => s.searchError);
  const bibEntries = useCitationStore((s) => s.bibEntries);
  const addError = useCitationStore((s) => s.addError);
  const clearAddError = useCitationStore((s) => s.clearAddError);
  const documentCiteKeys = useCitationStore((s) => s.documentCiteKeys);
  const citationStyle = useCitationStore((s) => s.citationStyle);
  const setCitationStyle = useCitationStore((s) => s.setCitationStyle);
  const search = useCitationStore((s) => s.search);
  const addCitation = useCitationStore((s) => s.addCitation);
  const removeCitation = useCitationStore((s) => s.removeCitation);
  const parseDocumentCitations = useCitationStore((s) => s.parseDocumentCitations);

  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [expandedAbstract, setExpandedAbstract] = useState<number | null>(null);

  useEffect(() => {
    if (documentContent != null) {
      parseDocumentCitations(documentContent);
    }
  }, [documentContent, parseDocumentCitations]);

  useEffect(() => {
    if (addError) {
      const t = setTimeout(clearAddError, 4000);
      return () => clearTimeout(t);
    }
  }, [addError, clearAddError]);

  const handleSearch = useCallback(() => {
    search(searchQuery);
  }, [search, searchQuery]);

  const handleAddCitation = useCallback(
    async (paper: SearchResult) => {
      const bibtex = await addCitation(paper);
      if (bibtex && editorView) {
        if (isLatex) {
          insertSnippet(editorView, `\n${bibtex}\n`);
        }
      }
    },
    [addCitation, editorView, isLatex],
  );

  const handleInsertCite = useCallback(
    (key: string, entry?: BibEntry) => {
      if (!editorView) return;
      if (isLatex) {
        insertSnippet(editorView, `\\cite{${key}}`);
      } else if (entry) {
        const idx = bibEntries.indexOf(entry);
        const inlineRef = formatInlineRef(entry, citationStyle, idx >= 0 ? idx : undefined);
        insertSnippet(editorView, inlineRef);
      }
    },
    [editorView, isLatex, bibEntries, citationStyle],
  );

  const handleCopyRef = useCallback(
    (key: string, entry?: BibEntry) => {
      let text: string;
      if (isLatex) {
        text = `\\cite{${key}}`;
      } else if (entry) {
        text = formatCitation(entry, citationStyle);
      } else {
        text = `\\cite{${key}}`;
      }
      navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    },
    [isLatex, citationStyle],
  );

  const orphanDocKeys = useMemo(
    () => documentCiteKeys.filter((k) => !bibEntries.some((e) => e.key === k)),
    [documentCiteKeys, bibEntries],
  );

  const hasContent = searchResults.length > 0 || bibEntries.length > 0 || orphanDocKeys.length > 0;
  const showEmptyState = !searching && !searchQuery && !hasContent;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header + style selector */}
      <div className="shrink-0 px-3 py-2">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
            Citations
          </span>
        </div>
        {/* Style pill toggle */}
        <div className="flex items-center gap-0.5 mt-1.5 rounded-[var(--radius-sm)] border border-[var(--border-hairline)] bg-[var(--bg-base)] p-0.5">
          {CITATION_STYLES.map((s) => (
            <button
              key={s.id}
              onClick={() => setCitationStyle(s.id)}
              className={cn(
                "relative flex-1 rounded-[3px] px-1.5 py-0.5 text-[9px] font-medium text-center",
                "transition-colors duration-75",
                citationStyle === s.id
                  ? "text-[var(--text-primary)]"
                  : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]",
              )}
            >
              {citationStyle === s.id && (
                <motion.div
                  layoutId="cite-style-pill"
                  className="absolute inset-0 rounded-[3px] bg-[var(--bg-surface)] shadow-sm border border-[var(--border-hairline)]"
                  transition={{ type: "spring", stiffness: 500, damping: 35, mass: 0.6 }}
                />
              )}
              <span className="relative z-[1]">{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="shrink-0 px-3 pb-2">
        <div className="relative">
          <MagnifyingGlass
            size={12}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
          />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Search papers..."
            className={cn(
              "w-full rounded-[var(--radius-md)] border border-[var(--border-hairline)]",
              "bg-[var(--bg-base)] pl-7 pr-3 py-1.5 text-[11px] text-[var(--text-primary)]",
              "placeholder:text-[var(--text-muted)] outline-none focus:border-[var(--border-default)]",
              "transition-colors duration-100",
            )}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden px-2">
        {/* Add error banner */}
        <AnimatePresence>
          {addError && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.15 }}
              className="mb-1 rounded-[var(--radius-sm)] bg-[var(--color-soft-red)]/10 px-2.5 py-1.5 text-[10px] text-[var(--color-soft-red)] break-words"
            >
              {addError}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── My Citations (always visible at top) ── */}
        {bibEntries.length > 0 && (
          <CitationSection title="My Citations">
            <AnimatePresence initial={false}>
              {bibEntries.map((entry, idx) => (
                <motion.div
                  key={entry.key}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <BibEntryRow
                    entry={entry}
                    index={idx}
                    citationStyle={citationStyle}
                    copiedKey={copiedKey}
                    onInsert={(k) => handleInsertCite(k, entry)}
                    onCopy={(k) => handleCopyRef(k, entry)}
                    onRemove={() => removeCitation(entry.key)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </CitationSection>
        )}

        {/* ── Document cite keys (not in My Citations) ── */}
        {orphanDocKeys.length > 0 && (
          <CitationSection title="In Document">
            {orphanDocKeys.map((key) => (
              <CiteKeyRow
                key={key}
                citeKey={key}
                copiedKey={copiedKey}
                onInsert={(k) => handleInsertCite(k)}
                onCopy={(k) => handleCopyRef(k)}
              />
            ))}
          </CitationSection>
        )}

        {/* ── Search state ── */}
        {searching && (
          <div className="flex items-center justify-center py-6">
            <div className="flex flex-col items-center gap-2">
              <CircleNotch size={16} className="animate-spin text-[var(--text-muted)]" />
              {searchError && (
                <p className="text-[10px] text-[var(--text-muted)] text-center break-words">{searchError}</p>
              )}
            </div>
          </div>
        )}

        {!searching && searchError && (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <p className="text-[11px] text-[var(--color-soft-red)] break-words">{searchError}</p>
            <button
              onClick={handleSearch}
              className={cn(
                "flex items-center gap-1 rounded-[var(--radius-sm)] px-2.5 py-1 text-[10px] font-medium",
                "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
                "bg-[var(--bg-surface-hover)] hover:bg-[var(--bg-surface-hover)]",
                "transition-colors duration-75",
              )}
            >
              <ArrowClockwise size={10} weight="bold" />
              Retry
            </button>
          </div>
        )}

        {/* ── Search results ── */}
        {!searching && !searchError && searchResults.length > 0 && (
          <CitationSection title="Search Results">
            {searchResults.map((r, i) => (
              <SearchResultCard
                key={i}
                result={r}
                index={i}
                expanded={expandedAbstract === i}
                onToggleAbstract={() =>
                  setExpandedAbstract(expandedAbstract === i ? null : i)
                }
                onAdd={() => handleAddCitation(r)}
                onRemove={() => {
                  const match = bibEntries.find(
                    (e) => e.title.toLowerCase() === r.title.toLowerCase(),
                  );
                  if (match) removeCitation(match.key);
                }}
                alreadyAdded={bibEntries.some(
                  (e) => e.title.toLowerCase() === r.title.toLowerCase(),
                )}
              />
            ))}
          </CitationSection>
        )}

        {!searching && !searchError && searchResults.length === 0 && searchQuery && (
          <div className="flex flex-col items-center py-6 text-center">
            <BookOpen size={18} className="text-[var(--text-muted)]" />
            <p className="mt-2 text-[11px] text-[var(--text-muted)]">
              No results. Try a different search.
            </p>
          </div>
        )}

        {/* Empty state */}
        {showEmptyState && (
          <div className="flex flex-col items-center gap-2 py-8 px-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-surface-hover)] border border-[var(--border-hairline)]">
              <BookOpen size={20} weight="duotone" className="text-[var(--text-muted)]" />
            </div>
            <div className="text-center">
              <p className="text-[11px] text-[var(--text-muted)]">
                Search for papers to cite
              </p>
              <p className="mt-0.5 text-[10px] text-[var(--text-muted)] opacity-60">
                Powered by Semantic Scholar
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────

function CitationSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-1 mb-1">
      <div className="px-1 py-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
          {title}
        </span>
      </div>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

function BibEntryRow({
  entry,
  index,
  citationStyle,
  copiedKey,
  onInsert,
  onCopy,
  onRemove,
}: {
  entry: BibEntry;
  index: number;
  citationStyle: CitationStyle;
  copiedKey: string | null;
  onInsert: (key: string) => void;
  onCopy: (key: string) => void;
  onRemove: () => void;
}) {
  const [showPreview, setShowPreview] = useState(false);
  const formatted = useMemo(
    () => formatCitation(entry, citationStyle),
    [entry, citationStyle],
  );
  const inlineRef = useMemo(
    () => formatInlineRef(entry, citationStyle, index),
    [entry, citationStyle, index],
  );

  return (
    <div
      className={cn(
        "rounded-[var(--radius-sm)] px-2 py-1.5",
        "bg-[var(--bg-surface)] border border-[var(--border-hairline)]",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <span className="text-[10px] font-mono text-[var(--text-secondary)] truncate block">
            {entry.key}
          </span>
          <span className="text-[9px] text-[var(--text-muted)] truncate block mt-0.5">
            {inlineRef}
          </span>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <IconAction
            icon={CursorText}
            title="Insert at cursor"
            onClick={() => onInsert(entry.key)}
            hoverColor="lever-orange"
          />
          <CopyAction
            citeKey={entry.key}
            copiedKey={copiedKey}
            onCopy={onCopy}
          />
          <IconAction
            icon={Trash}
            title="Remove"
            onClick={onRemove}
            hoverColor="soft-red"
          />
        </div>
      </div>
      {/* Formatted preview toggle */}
      <button
        onClick={() => setShowPreview(!showPreview)}
        className={cn(
          "mt-1 flex items-center gap-0.5 text-[9px] text-[var(--text-muted)]",
          "hover:text-[var(--text-secondary)] transition-colors duration-75",
        )}
      >
        <motion.span
          animate={{ rotate: showPreview ? 0 : -90 }}
          transition={{ duration: 0.12 }}
          className="inline-flex"
        >
          <CaretDown size={8} weight="bold" />
        </motion.span>
        Preview
      </button>
      <AnimatePresence>
        {showPreview && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15 }}
            className="mt-1 text-[9px] leading-relaxed text-[var(--text-secondary)] overflow-hidden italic break-words"
          >
            {formatted}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

function SearchResultCard({
  result,
  index: _index,
  expanded,
  onToggleAbstract,
  onAdd,
  onRemove,
  alreadyAdded,
}: {
  result: SearchResult;
  index: number;
  expanded: boolean;
  onToggleAbstract: () => void;
  onAdd: () => void;
  onRemove: () => void;
  alreadyAdded: boolean;
}) {
  const authorsText = useMemo(() => {
    if (!result.authors?.length) return "";
    const names = result.authors.slice(0, 3);
    const suffix = result.authors.length > 3 ? " et al." : "";
    return names.join(", ") + suffix;
  }, [result.authors]);

  return (
    <div
      className={cn(
        "rounded-[var(--radius-md)] p-2",
        "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75",
      )}
    >
      {/* Title */}
      <div className="flex items-start gap-1">
        {result.url ? (
          <a
            href={result.url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "text-[11px] font-medium leading-snug line-clamp-2 flex-1 min-w-0",
              "text-[var(--text-primary)] hover:text-[var(--color-lever-orange)]",
              "transition-colors duration-75",
            )}
          >
            {result.title}
          </a>
        ) : (
          <p className="text-[11px] font-medium text-[var(--text-primary)] leading-snug line-clamp-2 flex-1 min-w-0">
            {result.title}
          </p>
        )}
        {result.url && (
          <a
            href={result.url}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 mt-0.5 text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors duration-75"
          >
            <ArrowSquareOut size={10} />
          </a>
        )}
      </div>

      {/* Authors + year + journal */}
      <p className="text-[10px] text-[var(--text-muted)] mt-0.5 truncate">
        {authorsText}
        {result.year ? ` (${result.year})` : ""}
        {result.journal ? ` \u00B7 ${result.journal}` : ""}
      </p>

      {/* Abstract toggle */}
      {result.abstract && (
        <button
          onClick={onToggleAbstract}
          className={cn(
            "mt-1 flex items-center gap-0.5 text-[9px] text-[var(--text-muted)]",
            "hover:text-[var(--text-secondary)] transition-colors duration-75",
          )}
        >
          <motion.span
            animate={{ rotate: expanded ? 0 : -90 }}
            transition={{ duration: 0.12 }}
            className="inline-flex"
          >
            <CaretDown size={8} weight="bold" />
          </motion.span>
          Abstract
        </button>
      )}
      <AnimatePresence>
        {expanded && result.abstract && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15 }}
            className="mt-1 text-[10px] leading-relaxed text-[var(--text-secondary)] overflow-hidden break-words"
          >
            {result.abstract}
          </motion.p>
        )}
      </AnimatePresence>

      {/* Add / remove toggle */}
      {alreadyAdded ? (
        <motion.button
          onClick={onRemove}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.96 }}
          transition={spring}
          className={cn(
            "mt-1 flex items-center gap-1 rounded-[var(--radius-sm)] px-2 py-0.5 text-[10px] font-medium",
            "text-[var(--color-signal-green)] bg-[var(--color-signal-green)]/8",
            "hover:text-[var(--color-soft-red)] hover:bg-[var(--color-soft-red)]/8",
            "transition-colors duration-75 group/added",
          )}
        >
          <Check size={10} weight="bold" className="group-hover/added:hidden" />
          <Trash size={10} weight="bold" className="hidden group-hover/added:block" />
          <span className="group-hover/added:hidden">Added</span>
          <span className="hidden group-hover/added:inline">Remove</span>
        </motion.button>
      ) : (
        <motion.button
          onClick={onAdd}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.96 }}
          transition={spring}
          className={cn(
            "mt-1 flex items-center gap-1 rounded-[var(--radius-sm)] px-2 py-0.5 text-[10px] font-medium",
            "text-[var(--color-lever-orange)] bg-[var(--color-lever-orange)]/8",
            "hover:bg-[var(--color-lever-orange)]/15 transition-colors duration-75",
          )}
        >
          <Plus size={10} weight="bold" />
          Add to document
        </motion.button>
      )}
    </div>
  );
}

function CiteKeyRow({
  citeKey,
  copiedKey,
  onInsert,
  onCopy,
}: {
  citeKey: string;
  copiedKey: string | null;
  onInsert: (key: string) => void;
  onCopy: (key: string) => void;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-2 rounded-[var(--radius-sm)] px-2 py-1.5",
        "bg-[var(--bg-surface)] border border-[var(--border-hairline)]",
      )}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <HashStraight size={10} className="shrink-0 text-[var(--text-muted)]" />
        <span className="text-[10px] font-mono text-[var(--text-secondary)] truncate">
          {citeKey}
        </span>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        <IconAction
          icon={CursorText}
          title="Insert at cursor"
          onClick={() => onInsert(citeKey)}
          hoverColor="lever-orange"
        />
        <CopyAction citeKey={citeKey} copiedKey={copiedKey} onCopy={onCopy} />
      </div>
    </div>
  );
}

function IconAction({
  icon: Icon,
  title,
  onClick,
  hoverColor,
}: {
  icon: React.ComponentType<{ size?: number; weight?: IconWeight }>;
  title: string;
  onClick: () => void;
  hoverColor: "lever-orange" | "soft-red";
}) {
  return (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onClick={onClick}
      className={cn(
        "flex items-center justify-center w-5 h-5 rounded-[var(--radius-sm)]",
        "text-[var(--text-muted)] transition-colors duration-75",
        hoverColor === "lever-orange" &&
          "hover:text-[var(--color-lever-orange)] hover:bg-[var(--color-lever-orange)]/10",
        hoverColor === "soft-red" &&
          "hover:text-[var(--color-soft-red)] hover:bg-[var(--color-soft-red)]/10",
      )}
      title={title}
    >
      <Icon size={11} weight="bold" />
    </motion.button>
  );
}

function CopyAction({
  citeKey,
  copiedKey,
  onCopy,
}: {
  citeKey: string;
  copiedKey: string | null;
  onCopy: (key: string) => void;
}) {
  const isCopied = copiedKey === citeKey;
  return (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onClick={() => onCopy(citeKey)}
      className={cn(
        "flex items-center justify-center w-5 h-5 rounded-[var(--radius-sm)]",
        "transition-colors duration-75",
        isCopied
          ? "text-[var(--color-signal-green)]"
          : "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
      )}
      title="Copy reference"
    >
      <AnimatePresence mode="wait" initial={false}>
        {isCopied ? (
          <motion.span
            key="check"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            transition={{ duration: 0.12 }}
          >
            <Check size={11} weight="bold" />
          </motion.span>
        ) : (
          <motion.span
            key="copy"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            transition={{ duration: 0.12 }}
          >
            <Copy size={11} />
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
}
